import{ useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useI18n } from '../../../../../context/I18nContext';
import './AttachmentPreviewModal.css';
import { formatFileSize, getFileType, downloadDecryptedFile, sanitizeFilename } from '../../../../../utils/fileUtils';
import useFileDownload from '../../../../../hooks/useFileDownload';
import MessageReactions from './MessageReactions';
import DownloadProgressCircle from '../../../../Common/DownloadProgressCircle';
import SmartEmojiPicker from './SmartEmojiPicker';
import logger from '../../../../../utils/logger';
import ReactionDetailsModal from './ReactionDetailsModal';
import { useForwarding } from '../../../../../context/ForwardingContext';
import { useRoomContext } from '../../../../../context/RoomContext';
import { decryptMediaBlob } from '../../../../../utils/encryptionUtils';
import mediaCacheService from '../../../../../services/mediaCacheService';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import { useModal } from '../../../../../context/ModalContext';
import { canSendMessages } from '../../../../../utils/roomUtils';
import useMalwareToast from '../../../../../hooks/useMalwareToast';

const AttachmentPreviewModal = ({
  url,
  filename,
  msgtype,
  fileSize,
  mimeType,
  senderName,
  thumbnailUrl,
  message,
  encryptionInfo,
  onReplyMessage,
  onFocusMessage,  
  onToggleReaction,
  displayReactions,
  onClose,
}) => {  
  const [mediaSrc, setMediaSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const { downloadFileToDisplay, downloadFile } = useFileDownload();
  const [isReactionDetailsOpen, setReactionDetailsOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);  
  const reactions = displayReactions || message?._reactions;
  const roomId = message?.getRoomId();
  const isTextFile = mimeType && mimeType.includes('text/');
  const { startForwarding } = useForwarding();
  const { room, roomId: contextRoomId, powerLevelsCounter } = useRoomContext();
  const client = useMatrixClient();
  const { showError } = useModal();
  const { t } = useI18n();

  const { showMalwareToast, dismiss: dismissMalwareToast, markDownloaded } = useMalwareToast(message);

  // Check if user has permission to send messages
  const hasMessagePermission = useMemo(
    () => canSendMessages(room, client),
    [room, client, powerLevelsCounter],
  );

  const handleDownloadClick = useCallback(async () => {
    if (mediaSrc) {
      // Use already-loaded (and decrypted for encrypted rooms) content
      const link = document.createElement('a');
      link.href = mediaSrc;
      link.download = sanitizeFilename(filename);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Mark on attempt — the download is fire-and-forget once the anchor fires;
      // there is no browser callback to confirm the save completed.
      markDownloaded();
    } else if (encryptionInfo) {
      // Encrypted file not yet loaded — download + decrypt manually
      try {
        // snyk:ignore:DOM-based Cross-site Scripting (XSS)
        const ok = await downloadDecryptedFile({ downloadFileToDisplay, url, roomId, mimeType, encryptionInfo, filename });
        if (ok) markDownloaded();
      } catch (err) {
        logger.error('Encrypted download failed:', err);
        showError(t('AttachmentPreviewModal.downloadFailed'), t('AttachmentPreviewModal.downloadEncryptedError'));
      }
    } else {
      const ok = await downloadFile({ url, filename, mimeType });
      if (ok) markDownloaded();
    }
  }, [downloadFile, downloadFileToDisplay, url, filename, mimeType, mediaSrc, encryptionInfo, roomId, showError, t, message]);

  const handleReplyClick = useCallback(() => {
    onClose();
    onReplyMessage(message, senderName, filename, msgtype, filename, thumbnailUrl, fileSize);
  }, [onReplyMessage, onClose, message, senderName, filename, msgtype, thumbnailUrl, fileSize]);

  const handleFocusClick = useCallback(() => {
    onClose();
    onFocusMessage(message);
  }, [onFocusMessage, onClose, message]);

  const handleForwardClick = useCallback(() => {
    const messageContent = message.getContent();
    const caption = messageContent.body !== filename ? messageContent.body : null;
    
    const forwardContent = {
      body: filename,
      msgtype: msgtype,
      filename: filename,
      url: url,
      info: messageContent.info,
      caption: caption,
    };
    
    onClose();
    startForwarding(message, contextRoomId, forwardContent);
  }, [message, filename, msgtype, url, contextRoomId, startForwarding, onClose]);

  const handleReactClick = (e) => {
    e.stopPropagation();
    setShowEmojiPicker(prev => !prev);
  };

  // Close emoji picker when clicking outside of it
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const blobUrlRef = { current: null };

    const loadMedia = async () => {
      if (!url || !downloadFileToDisplay) {
        setError(t('AttachmentPreviewModal.previewNotAvailable'));
        setIsLoading(false);
        return;
      }

      try {
        logger.debug('[AttachmentPreviewModal] Loading media:', {
          url,
          encrypted: !!encryptionInfo,
          mimeType,
        });
        
        let blobUrl = await downloadFileToDisplay({
          url,
          roomId, // Pass roomId to the download function for caching
          mimeType, // Pass mimeType for Edge browser compatibility with blob URLs
          onProgress: (p) => setProgress(p.percent),
          signal: controller.signal, // Pass the signal to the download function
          skipCache: !!encryptionInfo, // Skip cache if encrypted
        });

        // If encrypted, decrypt the blob
        if (encryptionInfo) {
          try {
            const { decryptedBlobUrl, decryptedBlob } = await decryptMediaBlob(blobUrl, encryptionInfo, mimeType);
            blobUrl = decryptedBlobUrl;
            blobUrlRef.current = blobUrl;

            // Cache the decrypted blob
            try {
              await mediaCacheService.setMedia(url, decryptedBlob, roomId);
              logger.debug('[AttachmentPreviewModal] Cached decrypted attachment');
            } catch (cacheError) {
              logger.warn('[AttachmentPreviewModal] Failed to cache decrypted attachment:', cacheError);
            }
          } catch (decryptErr) {
            logger.error('[AttachmentPreviewModal] Decryption failed:', decryptErr);
            throw new Error(`Decryption failed: ${decryptErr.message}`);
          }
        } else {
          blobUrlRef.current = blobUrl; // Store for cleanup
        }

        if (isMounted) {
          // For text files, fetch the text content
          if (isTextFile) {
            try {
              const response = await fetch(blobUrl);
              if (response.ok) {
                const text = await response.text();
                setTextContent(text);
                setMediaSrc(blobUrl);
                setIsLoading(false);
              } else {
                throw new Error('Failed to fetch text content');
              }
            } catch (textError) {
              logger.error('Error loading text content:', textError);
              setError(t('AttachmentPreviewModal.failedLoadTextPreview'));
              setIsLoading(false);
            }
          } else {
            setMediaSrc(blobUrl);
            setIsLoading(false);
          }
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (e) {
        if (isMounted) {
          if (e.name === 'AbortError') {
            logger.debug('Media download cancelled on modal close.');
            return;
          }
          logger.error('Error loading media for modal:', e);
          setError(t('AttachmentPreviewModal.failedLoadPreview'));
          setIsLoading(false);
        }
      }
    };

    loadMedia();

    return () => {
      isMounted = false;
      // Abort the fetch request when the component unmounts (modal closes)
      controller.abort();
      // Revoke the blob URL on cleanup to prevent memory leaks
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [url, filename, mimeType, downloadFileToDisplay, roomId, isTextFile, encryptionInfo, t]);

  // Render the main media content (image, video, text, or fallback)
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="attachment-modal-loading-content">
          <DownloadProgressCircle progress={progress} size={60} />
          <span className="attachment-modal-loading-text">{t('AttachmentPreviewModal.loading')}</span>
        </div>
      );
    }
    if (error || !mediaSrc) {
      return <div className="attachment-modal-fallback-content"><span>{error || t('AttachmentPreviewModal.previewNotAvailable')}</span></div>;
    }

    if (msgtype === 'm.image') {
      return <img src={mediaSrc} alt={filename} className="attachment-modal-media" />;
    } else if (msgtype === 'm.video') {
      return (
        <video controls autoPlay src={mediaSrc} className="attachment-modal-media">
          Your browser does not support the video tag.
        </video>
      );
    } else if (isTextFile && textContent) {
      return (
        <div className="attachment-modal-text-content">
          <pre>{textContent}</pre>
        </div>
      );
    }

    // Fallback for audio, files, etc.
    return (
      <div className="attachment-modal-fallback-content">
        <span>{t('AttachmentPreviewModal.previewNotAvailable')}</span>
      </div>
    );
  };

  // Render the details block below the media
  const renderDetails = () => {
    const fileType = getFileType(mimeType, filename);
    const formattedSize = formatFileSize(fileSize);

    return (
      <div className="attachment-modal-details">
        <div className="attachment-modal-file-info">
          {filename}
        </div>
        <div className="attachment-modal-file-info">
          {fileType} {formattedSize && `(${formattedSize})`}
        </div>
        {mediaSrc && (
          <a
            href={mediaSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="attachment-modal-view-btn"
          >{t('AttachmentPreviewModal.viewFullScreen')}</a>
        )}
      </div>
    );
  };

  // Render the modal using a portal to ensure it's always on top
  return createPortal(
    <div className="attachment-modal-dark-wrapper" onClick={(e) => e.stopPropagation()}>
      <div className="attachment-modal-overlay">
        {/* UI controls are now direct children of the overlay for screen-relative positioning */}
        <button className="attachment-modal-close-btn" onClick={onClose} aria-label={t('AttachmentPreviewModal.closePreview')}>
          <i className="fa fa-times"></i>
        </button>
        <div className="attachment-modal-actions">
          <button className="attachment-modal-action-btn" title={t('AttachmentPreviewModal.download')} onClick={handleDownloadClick}>
            <i className="fa fa-cloud-download"></i>
          </button>
          {hasMessagePermission && (
            <button className="attachment-modal-action-btn" title={t('AttachmentPreviewModal.reply')} onClick={handleReplyClick}>
              <i className="fa fa-reply"></i>
            </button>
          )}
          {hasMessagePermission && (
            <button className="attachment-modal-action-btn" title={t('AttachmentPreviewModal.forward')} onClick={handleForwardClick}>
              <i className="fa fa-share"></i>
            </button>
          )}
          <button className="attachment-modal-action-btn" title={t('AttachmentPreviewModal.focus')} onClick={handleFocusClick}>
            <i className="fa fa-commenting"></i>
          </button>
          {hasMessagePermission && (
            <button className="attachment-modal-action-btn" title={t('AttachmentPreviewModal.react')} onClick={handleReactClick}>
              <img src='/images/svg/emoji-react.svg' alt='React' className="attachment-modal-react-icon" />
            </button>
          )}
        </div>
        <div className="attachment-modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
          <div className="attachment-modal-content">
            <div className="attachment-modal-media-wrapper">
              {renderContent()}
              {reactions && reactions.size > 0 && (
                <MessageReactions
                  reactions={reactions}
                  className="in-modal"
                  onReactionDetailsOpen={() => setReactionDetailsOpen(true)}
                />
              )}
            </div>
            {renderDetails()}
          </div>
        </div>
      </div>
      {showMalwareToast && (
        <div className="attachment-modal-malware-toast" role="alert">
          <i className="fa fa-shield attachment-modal-malware-toast-icon"></i>
          <span className="attachment-modal-malware-toast-text">
            {t('AttachmentPreviewModal.malwareWarningMessage')}
          </span>
          <button
            className="attachment-modal-malware-toast-dismiss"
            onClick={dismissMalwareToast}
            aria-label={t('AttachmentPreviewModal.dismissMalwareWarning')}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      )}
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="attachment-modal-emoji-picker-container"
          onClick={(e) => e.stopPropagation()}
        >
          <SmartEmojiPicker
            messageId={message.getId()}
            reactions={reactions}
            onToggleReaction={onToggleReaction}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
      )}
      {isReactionDetailsOpen && (
        <ReactionDetailsModal
          reactions={reactions}
          onClose={() => setReactionDetailsOpen(false)}
        />
      )}
    </div>,
    document.body,
  );
};

AttachmentPreviewModal.propTypes = {
  url: PropTypes.string, // The original mxc:// URL
  filename: PropTypes.string.isRequired,
  msgtype: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
  mimeType: PropTypes.string,
  senderName: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
  message: PropTypes.object.isRequired, // Matrix event object
  encryptionInfo: PropTypes.object, // Encryption metadata for decryption
  displayReactions: PropTypes.instanceOf(Map),
  onReplyMessage: PropTypes.func.isRequired,
  onFocusMessage: PropTypes.func.isRequired,
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AttachmentPreviewModal;